import { useCallback, useMemo } from 'react';

import { getApiBaseUrl } from '@/lib/config';
import { useCrmObjectSnapshots } from '@/lib/crm/hooks';

import { getAttachmentFileMeta } from './fileIdUtils';

function downloadFile(
  orgId: number,
  uploadedFileId: number,
  filename: string
): void {
  const url = `${getApiBaseUrl()}/api/orgs/${orgId}/files/${uploadedFileId}/download/`;
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function useRecordAttachmentDownloads(
  fileEntityIds: string[],
  orgId: number
) {
  // Hydrate the attached files here too (in addition to each row's own
  // subscription) so "Download all" can resolve every row's uploaded-file id
  // without threading hydration state up from the rows. Subscriptions are
  // deduped by the store, so this doesn't cause a second fetch.
  const attachmentSnapshots = useCrmObjectSnapshots(fileEntityIds, orgId);

  // "Download all" is only meaningful once every attachment snapshot has
  // hydrated (an unresolved snapshot has no uploaded-file id and would be
  // silently skipped) AND at least one attachment actually has a downloadable
  // uploaded-file id. Otherwise a click produces no downloads, so the action
  // stays disabled to avoid the silent no-op.
  const canDownloadAll = useMemo(() => {
    if (fileEntityIds.length === 0) {
      return false;
    }
    if (fileEntityIds.some(id => attachmentSnapshots.get(id) == null)) {
      return false;
    }
    return fileEntityIds.some(
      id =>
        getAttachmentFileMeta(attachmentSnapshots.get(id)).uploadedFileId !=
        null
    );
  }, [fileEntityIds, attachmentSnapshots]);

  const handleDownload = useCallback(
    (uploadedFileId: number, filename: string) => {
      downloadFile(orgId, uploadedFileId, filename);
    },
    [orgId]
  );

  // Fire every download synchronously within this click handler — no timers,
  // no cap. Browsers grant a short-lived "transient user activation" on a click
  // and allow multiple programmatic downloads within it (one bundled "download
  // multiple files" prompt); an `await`/`setTimeout` between clicks pushes the
  // later ones past that window, so the browser silently blocks them. Keeping
  // the loop fully synchronous downloads all attachments from the one gesture.
  const handleDownloadAll = useCallback(() => {
    for (const entityId of fileEntityIds) {
      const obj = attachmentSnapshots.get(entityId);
      const { filename, uploadedFileId } = getAttachmentFileMeta(obj);
      if (uploadedFileId == null) {
        continue;
      }
      downloadFile(orgId, uploadedFileId, filename);
    }
  }, [fileEntityIds, attachmentSnapshots, orgId]);

  return { canDownloadAll, handleDownload, handleDownloadAll };
}
